import {
  BusinessUnit,
  Publication,
  UniversalBuSpecificContent,
  UniversalConfigurationGroup,
  UniversalDesignObligation,
  UniversalInformation,
  UniversalMaturityLevelSequence,
  UniversalMaturityLevelSet,
  UniversalOperator,
  UniversalProcessModel,
  UniversalTailoringCriterion,
} from '@/gql/graphql';
import { isArray } from '@apollo/client/utilities';

import APP_CONFIG from '@/_config/config.app';
import { UniversalBuildingBlock, UniversalCompetency, UniversalDataBlock } from '@/gql/graphql';
import { UPMNode } from './maintainer.core.types';

/**
 * Retrieves configuration nodes either directly from the given configuration groups
 * or by aggregating the configuration nodes from the provided operators.
 *
 * If the provided configs array is non-empty, it is returned as is.
 * Otherwise, if operators are provided, their configuration nodes are combined into one array.
 * NOTE: Direct connection between configuration and node (TC, DO, BB) is not used. Connection is always established via operator node.
 * @param {UniversalConfigurationGroup[]} configs - An array of configuration groups.
 * @param {UniversalOperator[]} [operators] - Optional operators that may contain configuration nodes.
 * @returns {UniversalConfigurationGroup[]} An array of configuration nodes.
 * @example
 * const configurations = getConfigurationNodes(configs, operators);
 */
export const getConfigurationNodes = (configs: UniversalConfigurationGroup[], operators?: UniversalOperator[]) => {
  if (configs.length > 0) {
    return configs;
  }
  if (!operators) {
    return [];
  }
  const configurations = operators.reduce((acc, operator) => {
    return [...acc, ...operator.configurations];
  }, [] as UniversalConfigurationGroup[]);
  return configurations;
};

/**
 * Merges tailoring criteria from tailoring operators with optional additional criteria.
 * @param {UniversalOperator[]} tailoringOperators - Array of tailoring operator nodes.
 * @param {UniversalTailoringCriterion[]} [otherTC] - Optional additional tailoring criteria.
 * @returns {UniversalTailoringCriterion[]} Combined list of tailoring criteria.
 * @example
 * const criteria = getTailoringCriteriaNodes(operators, extraCriteria);
 */
export const getTailoringCriteriaNodes = (
  tailoringOperators: UniversalOperator[],
  otherTC?: UniversalTailoringCriterion[],
) => {
  const operators = tailoringOperators.reduce((acc, operator) => {
    return [...acc, ...operator.tailoringCriteria];
  }, [] as UniversalTailoringCriterion[]);
  return otherTC ? [...operators, ...otherTC] : operators;
};

/**
 * Retrieves business-specific content nodes from a UPM node.
 * @param {UPMNode} upm - The UPM node.
 * @returns {UniversalBuSpecificContent[]} An array of business-specific content nodes, or an empty array if none exist.
 * @example
 * const buContents = getBusinessSpecificContentNodes(upm);
 */
export const getBusinessSpecificContentNodes = (upm: UPMNode) => {
  if (!upm || !upm.__typename) {
    return [];
  }

  const SUPPORTS_BUC = new Set([
    'UniversalDesignObligation',
    'UniversalBuildingBlock',
    'UniversalDataBlock',
    'UniversalMaturityStage',
    'UniversalCompetency',
  ]);

  if (SUPPORTS_BUC.has(upm.__typename)) {
    return (upm as UniversalDesignObligation | UniversalBuildingBlock | UniversalDataBlock).buSpecificContents || [];
  }

  return [];
};

/**
 * Returns the business unit ids explicitly linked through BUC entries on the given node.
 * @param {UPMNode} [node] - The node to inspect for BUC-linked business units.
 * @returns {Set<string>} A set of business unit ids associated with the node's BUC content.
 */
export const getBuSpecificBusinessUnitIds = (node?: UPMNode): Set<string> => {
  if (!node) {
    return new Set();
  }

  return new Set(
    getBusinessSpecificContentNodes(node).flatMap((buSpecificContent) =>
      (buSpecificContent.businessUnits ?? []).map((businessUnit) => businessUnit.id),
    ),
  );
};

/**
 * Retrieves the maturity level sequence child nodes of an MLS node.
 *
 * Sequences are only surfaced as children when the parent MLS has more than one sequence.
 * A single-sequence MLS is rendered as a flat row.
 * @param {UPMNode} node - The UPM node.
 * @returns {UniversalMaturityLevelSequence[]} The sequence nodes, or empty array.
 */
export const getMaturityLevelSequenceNodes = (node: UPMNode): UniversalMaturityLevelSequence[] => {
  if (!node || node.__typename !== 'UniversalMaturityLevelSet') {
    return [];
  }
  const model = getMlsDisplayModel(node);
  return model.mode === 'nested' ? model.mlsqs : [];
};

export type MlsDisplayModel =
  | { mode: 'empty'; mls: UniversalMaturityLevelSet }
  | { mode: 'flat'; mls: UniversalMaturityLevelSet; mlsq: UniversalMaturityLevelSequence }
  | { mode: 'nested'; mls: UniversalMaturityLevelSet; mlsqs: UniversalMaturityLevelSequence[] };

/**
 * Display model for a Maturity Level Set node.
 *
 * Encapsulates the "1 MLSQ = flat row, multiple MLSQs = nested rows" branching rule in
 * a single, exhaustively-typed place so consumers switch on `mode` instead of counting
 * sequences inline. See `docs/features/maturity-level-set/display-model.md` for the
 * domain rationale.
 */
export const getMlsDisplayModel = (mls: UniversalMaturityLevelSet): MlsDisplayModel => {
  const sequences = mls.sequences ?? [];
  if (sequences.length === 0) {
    return { mode: 'empty', mls: mls };
  }
  if (sequences.length === 1) {
    return { mode: 'flat', mls: mls, mlsq: sequences[0] };
  }
  return { mode: 'nested', mls: mls, mlsqs: sequences };
};

/**
 * Finds the parent MLS node for a given MLSQ id, by walking `upm.maturityLevelSets`.
 * Returns undefined if the MLSQ is not attached to any MLS (orphan) or when upm is absent.
 */
export const getParentMlsForMlsq = (mlsqId: string, upm?: UniversalProcessModel) => {
  if (!upm) {
    return undefined;
  }
  return (upm.maturityLevelSets ?? []).find((mls) => (mls.sequences ?? []).some((sequence) => sequence.id === mlsqId));
};

/**
 * Retrieves operator nodes from a UPM node.
 *
 * For nodes of type 'UniversalDesignObligation' or 'UniversalBuildingBlock', returns their tailoringOperators property.
 * Otherwise, returns an empty array.
 * @param {UPMNode} upm - The UPM node.
 * @returns {UniversalOperator[]} The operator nodes associated with the UPM node.
 * @example
 * const operators = getOperatorNodes(someNode);
 */
export const getOperatorNodes = (upm: UPMNode) => {
  if (upm.__typename === 'UniversalDesignObligation') {
    return upm.tailoringOperators || [];
  }
  if (upm.__typename === 'UniversalBuildingBlock') {
    return upm.tailoringOperators || [];
  }
  return [];
};

/**
 * Retrieves the parent node of a business-specific content node from the given Universal Process Model.
 *
 * This function checks if the provided node is of type 'UniversalBuSpecificContent' and searches through its
 * `specified<NodeLabel>` properties to find the parent node. Then it gets the parent node in the UPM
 * based on the corresponding <NodeLabel> and id.
 * @param {UPMNode} [node] - The business-specific content node.
 * @param {UniversalProcessModel} [upm] - The Universal Process Model to search within.
 * @returns {UPMNode | undefined} The parent UPM node if found, otherwise undefined.
 * @example
 * const parent = getBucParent(buContentNode, upm);
 */
export const getBucParent = (node?: UPMNode, upm?: UniversalProcessModel): UPMNode | undefined => {
  if (!node) {
    return undefined;
  }
  if (!upm) {
    return undefined;
  }
  if (node.__typename !== 'UniversalBuSpecificContent') {
    return undefined;
  }
  const parentTypes = [
    { bucKey: 'specifiedDesignObligations', upmKey: 'designObligations' },
    { bucKey: 'specifiedBuildingBlocks', upmKey: 'buildingBlocks' },
    { bucKey: 'specifiedDataBlocks', upmKey: 'dataBlocks' },
    { bucKey: 'specifiedMaturityStages', upmKey: 'maturityStages' },
    { bucKey: 'specifiedCompetencies', upmKey: 'competencies' },
  ] as const satisfies Array<{ bucKey: keyof UniversalBuSpecificContent; upmKey: keyof UniversalProcessModel }>;

  // Based on publishing validations, currently we assume
  // only a single parent is present, so we only check the first element.
  const parentType = parentTypes.find((type) => node[type.bucKey]?.[0]?.id);
  if (!parentType) {
    return undefined;
  }

  const parentId = node[parentType.bucKey]?.[0]?.id;
  if (!parentId) {
    return undefined;
  }

  const parentNode = getNodeById(parentId, upm[parentType.upmKey]);
  return parentNode;
};

/**
 * Retrieves the parent UPM node for a given operator node from the Universal Process Model.
 *
 * Searches through the UPM for nodes that have enabled operator functionality and returns the node
 * containing the specified operator.
 * @param {UPMNode} [node] - The operator node to find the parent for.
 * @param {UniversalProcessModel} [upm] - The Universal Process Model to search within.
 * @returns {UPMNode | undefined} The parent node if found, otherwise undefined.
 * @example
 * const parent = getOpParent(operatorNode, upm);
 */
export const getOpParent = (node?: UPMNode, upm?: UniversalProcessModel): UPMNode | undefined => {
  if (!node) {
    return undefined;
  }
  if (!upm) {
    return undefined;
  }
  if (node.__typename !== 'UniversalOperator') {
    return undefined;
  }
  const opId = node.id;
  let parent: UPMNode | undefined;
  for (const key in upm) {
    const node = upm[key as keyof UniversalProcessModel];
    if (!node) {
      continue;
    }
    if (isArray(node)) {
      node.forEach((n) => {
        if (n.__typename) {
          if (APP_CONFIG.MAINTAINER.OP_ENABLED_NODES.includes(n.__typename)) {
            const found = getOperatorNodes(n).find((buc) => buc.id === opId);
            if (found) {
              parent = n;
            }
          }
        }
      });
    }
  }
  return parent;
};

/**
 * Retrieves the parent design obligation(s) for a given node.
 *
 * If the node is of type 'UniversalDesignObligation', returns the node wrapped in an array.
 * Otherwise, returns the node’s referencedDesignObligations or an empty array.
 * @param {UPMNode} [node] - The node to extract the design obligation(s) from.
 * @returns {UniversalDesignObligation[]} An array of design obligations.
 * @example
 * const parents = getParentDo(someNode);
 */
export const getParentDo = (node?: UPMNode): UniversalDesignObligation[] => {
  if (node?.__typename === 'UniversalDesignObligation') {
    return [node];
  } else {
    return node?.referencedDesignObligations || [];
  }
};

/**
 * Retrieves the name for a business-specific content node using its parent node.
 *
 * For nodes of type 'UniversalBuSpecificContent', returns the name of its parent node.
 * @param {UPMNode} [node] - The business-specific content node.
 * @param {UPMNode} [parent] - The parent node associated with the business-specific content.
 * @returns {string} The name derived from the parent node, or an empty string if not available.
 * @example
 * const name = getBucName(buContentNode, parentNode);
 */
export const getBucName = (node?: UPMNode, parent?: UPMNode) => {
  if (!node) {
    return '';
  }
  if (!parent) {
    return '';
  }
  if (node.__typename === 'UniversalBuSpecificContent') {
    return getNodeName(parent);
  }
  return '';
};

const BUC_PARENT_KEYS = [
  'specifiedDesignObligations',
  'specifiedBuildingBlocks',
  'specifiedDataBlocks',
  'specifiedMaturityStages',
  'specifiedCompetencies',
] as const satisfies Array<keyof UniversalBuSpecificContent>;

/**
 * Retrieves the display name for a BUC node's parent directly from the node itself, without
 * searching the UPM. Relies on `name` being requested on the `specified*` fields in
 * `MaintainerBucFragment`. Intended for display-only use (cards, tree, sort); edit flows that
 * need the full parent node should keep using `getBucParent`/`getBucName`.
 * @param {UPMNode} [node] - The business-specific content node.
 * @returns {string} The parent node's name, or an empty string if not available.
 * @example
 * const name = getBucParentName(bucNode);
 */
export const getBucParentName = (node?: UPMNode): string => {
  if (!node || node.__typename !== 'UniversalBuSpecificContent') {
    return '';
  }
  for (const key of BUC_PARENT_KEYS) {
    const name = node[key]?.[0]?.name;
    if (name) {
      return name;
    }
  }
  return '';
};

/**
 * Retrieves the name of a UPM node.
 *
 * - For a node of type 'UniversalInformation', returns its linkText.
 * - For a node of type 'UniversalTailoringCriterion', returns its question.
 * - For other node types, returns the node's name property.
 * @param {UPMNode} [node] - The node from which to extract the name.
 * @returns {string} The node name, or an empty string if not available.
 * @example
 * const name = getNodeName(someNode);
 */
export const getNodeName = (node?: UPMNode) => {
  if (!node) {
    return '';
  }
  if (node.__typename === 'UniversalTailoringCriterion') {
    return node.question;
  } else if (node.__typename === 'UniversalBuSpecificContent') {
    return node.businessUnits[0].name;
  } else if (node.__typename === 'UniversalOperator') {
    return node.type;
  } else if (node.__typename === 'UniversalMaturityLevelSequence') {
    // MLSQ has no `name` field of its own. On the home page it is rendered inside its parent
    // MLS card by `MlsNodeListItem`, so no name is needed. Other views (tree, tabs, details)
    // may fall back to the parent MLS's name via `getParentMlsForMlsq` when they need one.
    return '';
  } else {
    return (
      (
        node as Exclude<
          UPMNode,
          UniversalTailoringCriterion | UniversalBuSpecificContent | UniversalOperator | UniversalMaturityLevelSequence
        >
      ).name || ''
    );
  }
};

/**
 * Returns the publication status of a UPM node.
 *
 * Checks the node's __typename and returns its publication field if available;
 * otherwise, returns a default value (Publication.Published) or an empty string if the node is undefined.
 * @param {UPMNode} [node] - The node for which to retrieve the publication status.
 * @returns {Publication | string} The publication status of the node.
 * @example
 * const publication = getNodePublication(someNode);
 */
export const getNodePublication = (node?: UPMNode) => {
  if (!node) {
    return '';
  }

  if ('publication' in node) {
    return node.publication;
  }

  return Publication.Published;
};

/**
 * Returns the description of a UPM node.
 * @param {UPMNode} [node] - The node for which to retrieve the description.
 * @returns {string} The description string, or an empty string if not available.
 * @example
 * const description = getNodeDescription(someNode);
 */
export const getNodeDescription = (node?: UPMNode) => {
  if (!node) {
    return '';
  }
  if (node.__typename === 'UniversalBuildingBlock') {
    return node.targetState || '';
  }
  if (
    node.__typename === 'UniversalDesignObligation' ||
    node.__typename === 'UniversalDataBlock' ||
    node.__typename === 'UniversalMaturityStage' ||
    node.__typename === 'UniversalCompetency'
  ) {
    return node.description || '';
  }
  return '';
};

/**
 * Returns an array of business units associated with the given UPM node.
 * @param {UPMNode} [node] - The node from which to extract business units.
 * @returns {BusinessUnit[]} An array of business units, or an empty array if none are associated.
 * @example
 * const businessUnits = getNodeBusinessUnits(someNode);
 */
export const getNodeBusinessUnits = (node?: UPMNode): BusinessUnit[] => {
  let businessUnits: BusinessUnit[] = [];
  if (!node) {
    return businessUnits;
  }
  if (
    node.__typename === 'UniversalDesignObligation' ||
    node.__typename === 'UniversalMaturityStage' ||
    node.__typename === 'UniversalCompetency' ||
    node.__typename === 'UniversalInformation' ||
    node.__typename === 'UniversalBuildingBlock'
  ) {
    businessUnits = node.businessUnits;
  }
  if (node.__typename === 'UniversalDataBlock') {
    businessUnits = node.parentBuildingBlocks.map((bb) => bb.businessUnits).flat();
  }
  if (node.__typename === 'UniversalMaturityLevelSequence') {
    businessUnits = node.businessUnits;
  }
  if (node.__typename === 'UniversalBuSpecificContent') {
    businessUnits = node.businessUnits;
  }

  return businessUnits;
};

/**
 * Returns an array of building blocks associated with the given UPM node.
 * @param {UPMNode} [node] - The node from which to extract building blocks.
 * @returns {UniversalBuildingBlock[]} An array of building blocks.
 * @example
 * const buildingBlocks = getNodeBuildingBlocks(someNode);
 */
export const getNodeBuildingBlocks = (node?: UPMNode): UniversalBuildingBlock[] => {
  let buildingBlocks: UniversalBuildingBlock[] = [];
  if (!node) {
    return buildingBlocks;
  }
  if (node.__typename === 'UniversalDesignObligation') {
    buildingBlocks = node.buildingBlocks;
  }

  return buildingBlocks;
};

/**
 * Returns an array of tailoring operator nodes associated with the given UPM node.
 * @param {UPMNode} [node] - The node from which to extract tailoring operators.
 * @returns {UniversalOperator[]} An array of tailoring operator nodes.
 * @example
 * const operators = getNodeTailoringOperators(someNode);
 */
export const getNodeTailoringOperators = (node?: UPMNode): UniversalOperator[] => {
  if (!node) {
    return [];
  }
  if (node?.__typename === 'UniversalDesignObligation') {
    return node.tailoringOperators;
  }
  if (node?.__typename === 'UniversalBuildingBlock') {
    return node.tailoringOperators;
  }
  return [];
};

/**
 * Returns the configuring operator nodes associated with a given UPM node.
 * @param {UPMNode} [node] - The UPM node from which to extract configuring operators.
 * @returns {UniversalOperator[]} An array of configuring operators associated with the node.
 * @example
 * const operators = getConfigurationOperators(someNode);
 */
export const getConfigurationOperators = (node?: UPMNode): UniversalOperator[] => {
  if (!node) {
    return [];
  }
  if (node?.__typename === 'UniversalDesignObligation') {
    return node.configuringOperators;
  }
  if (node?.__typename === 'UniversalBuildingBlock') {
    return node.configuringOperators;
  }
  if (node?.__typename === 'UniversalTailoringCriterion') {
    return node.configuringOperators;
  }
  return [];
};

/**
 * Returns an array of UniversalDataBlock objects associated with the given node.
 * @param {UPMNode} [node] - The node from which to extract data blocks.
 * @returns {UniversalDataBlock[]} An array of UniversalDataBlock objects, or an empty array if none exist.
 * @example
 * const dataBlocks = getNodeDataBlocks(someNode);
 */
export const getNodeDataBlocks = (node?: UPMNode): UniversalDataBlock[] => {
  if (!node) {
    return [];
  }
  if (node?.__typename === 'UniversalBuildingBlock') {
    return node.dataBlocks;
  }
  return [];
};

/**
 * Returns an array of UniversalInformation associated with the given node.
 * @param {UPMNode} [node] - The node from which to extract informations.
 * @returns {UniversalInformation[]} An array of UniversalInformation, or an empty array if none exist.
 * @example
 * const informations = getNodeInformations(someNode);
 */
export const getNodeInformations = (node?: UPMNode): UniversalInformation[] => {
  if (!node) {
    return [];
  }
  if (node?.__typename === 'UniversalDesignObligation') {
    return node.informations;
  }
  if (node?.__typename === 'UniversalBuildingBlock') {
    return node.informations;
  }
  if (node?.__typename === 'UniversalDataBlock') {
    return node.informations;
  }
  if (node?.__typename === 'UniversalMaturityStage') {
    return node.informations;
  }
  return [];
};

/**
 * Retrieves the parent design obligation(s) for a given UPM node.
 * @param {UPMNode} [node] - The node to extract the design obligations from.
 * @returns {UniversalDesignObligation[]} An array of associated design obligations, or an empty array if none exist.
 * @example
 * const parents = getNodeParentDesignObligation(someNode);
 */
export const getNodeParentDesignObligation = (node?: UPMNode): UniversalDesignObligation[] => {
  if (!node || !node.__typename) {
    return [];
  }

  if (node.__typename === 'UniversalBuildingBlock') {
    return node.parentDesignObligations || [];
  }

  const HAS_REFERENCED_DO = new Set([
    'UniversalDataBlock',
    'UniversalTailoringCriterion',
    'UniversalBuSpecificContent',
    'UniversalCompetency',
  ]);

  if (HAS_REFERENCED_DO.has(node.__typename)) {
    return node.referencedDesignObligations || [];
  }

  return [];
};

/**
 * Returns an array of competencies associated with the given node.
 * @param {UPMNode} [node] - The node from which to extract competencies.
 * @returns {UniversalCompetency[]} An array of competencies if available, otherwise an empty array.
 * @example
 * const competencies = getNodeCompetencies(someNode);
 */
export const getNodeCompetencies = (node?: UPMNode): UniversalCompetency[] => {
  if (!node) {
    return [];
  }
  if (node.__typename === 'UniversalDataBlock') {
    return node.competencies;
  }
  return [];
};

/**
 * Returns a UPM node by its ID from a list of nodes.
 * @param {string} id - The ID of the node to find.
 * @param {UPMNode[]} nodes - The list of nodes to search through.
 * @returns {UPMNode | undefined} The node with the specified ID, or undefined if not found.
 * @example
 * const node = getNodeById('123', nodes);
 */
export const getNodeById = (id: string, nodes: UPMNode[]) => {
  return nodes.find((node) => node.id === id);
};

/**
 * Extracts the change origin from a UPM node based on its type.
 *
 * This selector function safely retrieves the `changeOrigin` property from various
 * UPM node types. The change origin indicates where or how a particular node was
 * modified or created in the system. Some node types (like UniversalConfigurationGroup)
 * do not have a change origin and will return undefined.
 *
 * @param {UPMNode} node - The UPM node to extract the change origin from
 * @returns {string | undefined} The change origin value if the node type supports it, undefined otherwise
 *
 * @example
 * ```typescript
 * const designObligation = getNodeById('do-123', flatUPM);
 * const origin = getNodeChangeOrigin(designObligation);
 *
 * if (origin) {
 *   console.log('Node was changed via:', origin);
 * }
 * ```
 */
export const getNodeChangeOrigin = (node: UPMNode) => {
  if ('changeOrigin' in node) {
    return node.changeOrigin;
  }

  return undefined;
};

/**
 * Helper function to get business units for BUC add/edit forms, filtering out business units already used by
 * sibling BUCs and optionally filtering by permitted business units.
 *
 * @param buc The BUC being edited (undefined if adding a new BUC)
 * @param parentNodeOfBuc The parent node of the BUC being added/edited
 * @param permittedBusinessUnits Optional list of permitted business units so that only a subset of those will be returned as options
 * @returns List of business units that can be selected for the BUC
 */
export function getBusForBucAddOrEdit(
  buc: UniversalBuSpecificContent | undefined,
  parentNodeOfBuc: UPMNode | undefined,
  permittedBusinessUnits?: BusinessUnit[],
) {
  let businessUnits = getNodeBusinessUnits(parentNodeOfBuc);

  if (parentNodeOfBuc) {
    // Filter out business units already used by sibling BUCs.
    const siblingBucsBuIds = new Set<string>();
    getBusinessSpecificContentNodes(parentNodeOfBuc).forEach((bus) =>
      bus.businessUnits.forEach((bu) => siblingBucsBuIds.add(bu.id)),
    );
    // In case of edit, allow the current BUC's business unit(s)
    buc?.businessUnits.forEach((bu) => siblingBucsBuIds.delete(bu.id));

    businessUnits = businessUnits.filter((bu) => !siblingBucsBuIds.has(bu.id));
  }

  // Filter by permitted business units if provided
  if (permittedBusinessUnits) {
    const permittedIds = new Set(permittedBusinessUnits.map((bu) => bu.id));
    businessUnits = businessUnits.filter((bu) => permittedIds.has(bu.id));
  }

  return businessUnits;
}
